import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Loader2, User, Lightbulb, Clock, CheckCircle, XCircle, Search, Eye } from "lucide-react";
import { format } from "date-fns";
import { useAdminMentorshipTopicRequests, useUpdateMentorshipTopicRequest, MentorshipTopicRequest } from "@/hooks/useMentorshipTopicRequests";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const STATUS_CONFIG = {
  pending_admin: { label: "Pending", color: "bg-amber-500", icon: Clock },
  in_progress: { label: "In Progress", color: "bg-blue-500", icon: Loader2 },
  fulfilled: { label: "Fulfilled", color: "bg-green-500", icon: CheckCircle },
  closed: { label: "Closed", color: "bg-muted", icon: XCircle },
};

export function MentorshipTopicRequestsManager() {
  const [statusFilter, setStatusFilter] = useState<string>("pending_admin");
  const [selectedRequest, setSelectedRequest] = useState<MentorshipTopicRequest | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [mentorSearch, setMentorSearch] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [selectedMentorId, setSelectedMentorId] = useState<string | null>(null);

  const { data: requests, isLoading } = useAdminMentorshipTopicRequests(statusFilter);
  const updateRequest = useUpdateMentorshipTopicRequest();

  // Fetch available mentors for matching
  const { data: mentors } = useQuery({
    queryKey: ["available-mentors", mentorSearch],
    queryFn: async () => {
      let query = supabase
        .from("profiles")
        .select(`
          id, 
          full_name, 
          avatar_url,
          mentor_profiles!inner(is_available_for_mentorship)
        `)
        .eq("role", "mentor")
        .eq("is_published", true)
        .limit(20);

      if (mentorSearch) {
        query = query.ilike("full_name", `%${mentorSearch}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: showDetailDialog,
  });

  const handleViewDetail = (request: MentorshipTopicRequest) => {
    setSelectedRequest(request);
    setAdminNotes(request.admin_notes || "");
    setSelectedMentorId(request.matched_mentor_id);
    setShowDetailDialog(true);
  };

  const handleUpdateStatus = async (status: string) => {
    if (!selectedRequest) return;
    
    await updateRequest.mutateAsync({
      id: selectedRequest.id,
      updates: { status },
    });
    
    setSelectedRequest(prev => prev ? { ...prev, status: status as any } : null);
  };

  const handleSaveNotes = async () => {
    if (!selectedRequest) return;
    
    await updateRequest.mutateAsync({
      id: selectedRequest.id,
      updates: { admin_notes: adminNotes },
    });
  };

  const handleMatchMentor = async () => {
    if (!selectedRequest || !selectedMentorId) return;
    
    await updateRequest.mutateAsync({
      id: selectedRequest.id,
      updates: { 
        matched_mentor_id: selectedMentorId,
        status: "fulfilled",
      },
    });
    
    setShowDetailDialog(false);
  };

  const getTopicLabel = (request: MentorshipTopicRequest) => {
    if (request.skill) return `Skill: ${request.skill.name}`;
    if (request.industry) return `Industry: ${request.industry.name}`;
    if (request.mentor_topic) return `Topic: ${request.mentor_topic.name}`;
    return "Unknown";
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Topic-Based Mentorship Requests
          </CardTitle>
          <CardDescription>
            Review and match entrepreneurs with mentors based on their topic requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex items-center gap-4 mb-4">
            <Label>Status:</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Requests</SelectItem>
                <SelectItem value="pending_admin">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="fulfilled">Fulfilled</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Request List */}
          {requests && requests.length > 0 ? (
            <div className="border rounded-lg divide-y">
              {requests.map((request) => {
                const StatusIcon = STATUS_CONFIG[request.status]?.icon || Clock;
                return (
                  <div
                    key={request.id}
                    className="p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={request.user?.avatar_url || undefined} />
                        <AvatarFallback>
                          {request.user?.full_name?.charAt(0) || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{request.user?.full_name || "Unknown User"}</p>
                        <p className="text-sm text-muted-foreground">{getTopicLabel(request)}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(request.created_at), "MMM d, yyyy 'at' h:mm a")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge 
                        variant="secondary" 
                        className={`${STATUS_CONFIG[request.status]?.color} text-white`}
                      >
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {STATUS_CONFIG[request.status]?.label}
                      </Badge>
                      <Button variant="outline" size="sm" onClick={() => handleViewDetail(request)}>
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Lightbulb className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No {statusFilter !== "all" ? statusFilter.replace("_", " ") : ""} requests found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Request Details</DialogTitle>
            <DialogDescription>
              Review and process this mentorship topic request
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-6 pr-4">
                {/* Requester Info */}
                <div className="flex items-center gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={selectedRequest.user?.avatar_url || undefined} />
                    <AvatarFallback>
                      {selectedRequest.user?.full_name?.charAt(0) || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedRequest.user?.full_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Submitted {format(new Date(selectedRequest.created_at), "MMM d, yyyy")}
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Request Details */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-muted-foreground">Requested Topic</Label>
                    <p className="font-medium">{getTopicLabel(selectedRequest)}</p>
                  </div>

                  <div>
                    <Label className="text-muted-foreground">Message</Label>
                    <p className="text-sm whitespace-pre-wrap bg-muted/50 p-3 rounded-lg">
                      {selectedRequest.request_message}
                    </p>
                  </div>

                  {selectedRequest.preferred_outcome && (
                    <div>
                      <Label className="text-muted-foreground">Preferred Outcome</Label>
                      <p className="text-sm">{selectedRequest.preferred_outcome}</p>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Status Update */}
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={selectedRequest.status} 
                    onValueChange={handleUpdateStatus}
                    disabled={updateRequest.isPending}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending_admin">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="fulfilled">Fulfilled</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Admin Notes */}
                <div className="space-y-2">
                  <Label>Admin Notes (internal only)</Label>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add internal notes about this request..."
                    rows={3}
                  />
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleSaveNotes}
                    disabled={updateRequest.isPending}
                  >
                    Save Notes
                  </Button>
                </div>

                <Separator />

                {/* Mentor Matching */}
                <div className="space-y-3">
                  <Label>Match with Mentor</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search mentors..."
                      value={mentorSearch}
                      onChange={(e) => setMentorSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  
                  {mentors && mentors.length > 0 && (
                    <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                      {mentors.map((mentor) => {
                        const isAvailable = (mentor.mentor_profiles as any)?.is_available_for_mentorship;
                        return (
                          <div
                            key={mentor.id}
                            className={`p-3 flex items-center justify-between cursor-pointer hover:bg-muted/50 ${
                              selectedMentorId === mentor.id ? "bg-primary/10" : ""
                            }`}
                            onClick={() => setSelectedMentorId(mentor.id)}
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={mentor.avatar_url || undefined} />
                                <AvatarFallback>{mentor.full_name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <span className="text-sm font-medium">{mentor.full_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {!isAvailable && (
                                <Badge variant="outline" className="text-xs">Unavailable</Badge>
                              )}
                              {selectedMentorId === mentor.id && (
                                <CheckCircle className="h-4 w-4 text-primary" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {selectedRequest.matched_mentor && (
                    <div className="bg-green-50 dark:bg-green-950/20 p-3 rounded-lg">
                      <p className="text-sm text-green-700 dark:text-green-400">
                        Currently matched with: <strong>{selectedRequest.matched_mentor.full_name}</strong>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
            <Button 
              onClick={handleMatchMentor}
              disabled={!selectedMentorId || updateRequest.isPending}
            >
              {updateRequest.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Match & Fulfill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
